import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Button,
  Card,
  Divider,
  Menu,
  Switch,
  Text,
  TextInput,
} from 'react-native-paper';
import type {
  CumulativeReportConfigRevision,
  CumulativeReportData,
  CumulativeReportMatchCondition,
  CumulativeReportMatchOperator,
  CumulativeReportMetric,
  CumulativeReportMetricResult,
  CumulativeReportConfigRevisionInput,
} from '@exhale/contracts';
import { colors, spacing, typography } from '../../../constants/theme';
import { adminService } from '../../../services/adminService';
import {
  adminCumulativeReportService,
  cumulativeReportEffectiveFromForDate,
  cumulativeReportPeriodForMonth,
  shiftCumulativeReportMonth,
} from '../../../services/adminCumulativeReportService';
import type {
  QuestionField,
  ReviewCategory,
  ReviewItem,
  ReviewTemplate,
} from '../AdminDashboard.types';

type MetricKind = CumulativeReportMetric['kind'];

interface MetricQuestion {
  categoryId: number;
  id: string;
  itemId: number;
  label: string;
  type?: string;
}

interface AdminCumulativeReportsSectionProps {
  categories: ReviewCategory[];
  items: ReviewItem[];
  onTemplateUpdated: (template: ReviewTemplate) => void;
  template: ReviewTemplate;
}

const metricKinds: Array<{ kind: MetricKind; label: string }> = [
  { kind: 'completed_item_count', label: 'Completed item count' },
  { kind: 'completed_category_count', label: 'Completed category count' },
  { kind: 'numeric_question_sum', label: 'Numeric answer sum' },
  { kind: 'question_match_count', label: 'Matching answer count' },
  { kind: 'conditional_numeric_sum', label: 'Conditional numeric sum' },
];

const operators: Array<{ operator: CumulativeReportMatchOperator; label: string }> = [
  { operator: 'equals', label: 'Equals' },
  { operator: 'not_equals', label: 'Does not equal' },
  { operator: 'in', label: 'Is one of' },
  { operator: 'truthy', label: 'Is answered / true' },
  { operator: 'falsy', label: 'Is empty / false' },
];

const currentMonth = () => new Date().toISOString().slice(0, 7);

const cloneMetrics = (metrics: CumulativeReportMetric[] | undefined): CumulativeReportMetric[] =>
  JSON.parse(JSON.stringify(metrics || []));

const latestRevision = (
  template: ReviewTemplate
): CumulativeReportConfigRevision | undefined =>
  [...(template.cumulativeReportConfig?.revisions || [])]
    .filter(revision => Number.isFinite(new Date(revision.effectiveFrom).getTime()))
    .sort((left, right) =>
      new Date(right.effectiveFrom).getTime() - new Date(left.effectiveFrom).getTime()
    )[0];

const initialRevision = (
  template: ReviewTemplate
): CumulativeReportConfigRevision | undefined =>
  [...(template.cumulativeReportConfig?.revisions || [])]
    .filter(revision => Number.isFinite(new Date(revision.effectiveFrom).getTime()))
    .sort((left, right) =>
      new Date(left.effectiveFrom).getTime() - new Date(right.effectiveFrom).getTime()
    )[0];

const metricKindLabel = (kind: MetricKind) =>
  metricKinds.find(option => option.kind === kind)?.label || kind;

const operatorLabel = (operator: CumulativeReportMatchOperator) =>
  operators.find(option => option.operator === operator)?.label || operator;

const conditionValueText = (condition: CumulativeReportMatchCondition): string => {
  if (Array.isArray(condition.value)) return condition.value.join(', ');
  if (condition.value === null) return '';
  return condition.value === undefined ? '' : String(condition.value);
};

const setConditionValue = (
  condition: CumulativeReportMatchCondition,
  value: string
): CumulativeReportMatchCondition => ({
  ...condition,
  ...(condition.operator === 'in'
    ? { value: value.split(',').map(entry => entry.trim()).filter(Boolean) }
    : { value }),
});

const metricScope = (metric: CumulativeReportMetric): 'item' | 'category' =>
  'categoryId' in metric && metric.categoryId !== undefined ? 'category' : 'item';

const isNumericMetric = (metric: CumulativeReportMetric): boolean =>
  metric.kind === 'numeric_question_sum' || metric.kind === 'conditional_numeric_sum';

const categoryIdsInScope = (
  categoryId: number,
  includeDescendants: boolean,
  categories: ReviewCategory[]
): Set<number> => {
  const result = new Set<number>([categoryId]);
  if (!includeDescendants) return result;

  let changed = true;
  while (changed) {
    changed = false;
    for (const category of categories) {
      if (
        category.parentCategoryId != null &&
        result.has(Number(category.parentCategoryId)) &&
        !result.has(category.id)
      ) {
        result.add(category.id);
        changed = true;
      }
    }
  }
  return result;
};

const questionsForMetricScope = (
  metric: CumulativeReportMetric,
  questions: MetricQuestion[],
  categories: ReviewCategory[]
): MetricQuestion[] => {
  if ('itemId' in metric && metric.itemId !== undefined) {
    return questions.filter(question => question.itemId === Number(metric.itemId));
  }
  if ('categoryId' in metric && metric.categoryId !== undefined) {
    const categoryIds = categoryIdsInScope(
      Number(metric.categoryId),
      Boolean(metric.includeDescendants),
      categories
    );
    return questions.filter(question => categoryIds.has(question.categoryId));
  }
  return [];
};

const compatiblePrimaryQuestions = (
  metric: CumulativeReportMetric,
  questions: MetricQuestion[]
): MetricQuestion[] => isNumericMetric(metric)
  ? questions.filter(question => question.type === 'number')
  : questions;

const primaryQuestionId = (metric: CumulativeReportMetric): string => {
  if (metric.kind === 'question_match_count') return metric.match.questionId;
  if (metric.kind === 'numeric_question_sum' || metric.kind === 'conditional_numeric_sum') {
    return metric.questionId;
  }
  return '';
};

const resetMetricQuestionIds = (
  metric: CumulativeReportMetric,
  questions: MetricQuestion[],
  categories: ReviewCategory[],
  preserveCurrent: boolean
): CumulativeReportMetric => {
  if (
    metric.kind !== 'numeric_question_sum' &&
    metric.kind !== 'question_match_count' &&
    metric.kind !== 'conditional_numeric_sum'
  ) {
    return metric;
  }

  const scopedQuestions = questionsForMetricScope(metric, questions, categories);
  const primaryOptions = compatiblePrimaryQuestions(metric, scopedQuestions);
  const primary = (preserveCurrent
    ? primaryOptions.find(question => question.id === primaryQuestionId(metric))
    : undefined) || primaryOptions[0];

  if (metric.kind === 'numeric_question_sum') {
    return { ...metric, questionId: primary?.id || '' };
  }
  if (metric.kind === 'question_match_count') {
    return {
      ...metric,
      match: { ...metric.match, questionId: primary?.id || '' },
    };
  }

  const conditionOptions = primary
    ? scopedQuestions.filter(question => question.itemId === primary.itemId)
    : [];
  const condition = (preserveCurrent
    ? conditionOptions.find(question => question.id === metric.when.questionId)
    : undefined) || conditionOptions[0];
  return {
    ...metric,
    questionId: primary?.id || '',
    when: { ...metric.when, questionId: condition?.id || '' },
  };
};

const metricHasValidReferences = (
  metric: CumulativeReportMetric,
  items: ReviewItem[],
  categories: ReviewCategory[],
  questions: MetricQuestion[]
): boolean => {
  if (metric.kind === 'completed_item_count') {
    return items.some(item => item.id === Number(metric.itemId));
  }
  if (metric.kind === 'completed_category_count') {
    return categories.some(category => category.id === Number(metric.categoryId));
  }

  const scopedQuestions = questionsForMetricScope(metric, questions, categories);
  const primary = compatiblePrimaryQuestions(metric, scopedQuestions).find(question =>
    question.id === primaryQuestionId(metric)
  );
  if (!primary) return false;
  if (metric.kind !== 'conditional_numeric_sum') return true;
  return scopedQuestions.some(question =>
    question.itemId === primary.itemId && question.id === metric.when.questionId
  );
};

const changeMetricScope = (
  metric: CumulativeReportMetric,
  scope: 'item' | 'category',
  items: ReviewItem[],
  categories: ReviewCategory[],
  questions: MetricQuestion[]
): CumulativeReportMetric => {
  const next = { ...metric } as CumulativeReportMetric & {
    itemId?: number;
    categoryId?: number;
    includeDescendants?: boolean;
  };
  delete next.itemId;
  delete next.categoryId;
  delete next.includeDescendants;

  const compatibleQuestion = compatiblePrimaryQuestions(metric, questions)[0];
  if (scope === 'category') {
    next.categoryId = compatibleQuestion?.categoryId || categories[0]?.id || 0;
    next.includeDescendants = true;
  } else {
    next.itemId = compatibleQuestion?.itemId || items[0]?.id || 0;
  }
  return resetMetricQuestionIds(next, questions, categories, false);
};

const createMetric = (
  kind: MetricKind,
  id: string,
  items: ReviewItem[],
  categories: ReviewCategory[],
  questions: MetricQuestion[]
): CumulativeReportMetric => {
  const itemId = items[0]?.id || 0;
  const categoryId = categories[0]?.id || 0;
  const question = questions[0];
  const numericQuestion = questions.find(candidate => candidate.type === 'number');
  switch (kind) {
    case 'completed_item_count':
      return { id, kind, label: 'Completed items', itemId };
    case 'completed_category_count':
      return { id, kind, label: 'Completed category items', categoryId, includeDescendants: true };
    case 'numeric_question_sum':
      return {
        id,
        kind,
        label: 'Total',
        itemId: numericQuestion?.itemId || itemId,
        questionId: numericQuestion?.id || '',
      };
    case 'question_match_count':
      return {
        id,
        kind,
        label: 'Matches',
        itemId: question?.itemId || itemId,
        match: { questionId: question?.id || '', operator: 'truthy' },
      };
    case 'conditional_numeric_sum':
      return {
        id,
        kind,
        label: 'Conditional total',
        itemId: numericQuestion?.itemId || itemId,
        questionId: numericQuestion?.id || '',
        when: { questionId: numericQuestion?.id || '', operator: 'truthy' },
      };
  }
};

const MetricSourceMenu: React.FC<{
  options: Array<{ id: string; label: string }>;
  onSelect: (id: string) => void;
  selectedId: string;
  testID: string;
}> = ({ options, onSelect, selectedId, testID }) => {
  const [visible, setVisible] = useState(false);
  const selected = options.find(option => option.id === selectedId);
  return (
    <Menu
      visible={visible}
      onDismiss={() => setVisible(false)}
      anchor={(
        <Button testID={testID} mode="outlined" onPress={() => setVisible(true)}>
          {selected?.label || 'Choose source'}
        </Button>
      )}
    >
      {options.map(option => (
        <Menu.Item
          key={option.id}
          title={option.label}
          onPress={() => {
            onSelect(option.id);
            setVisible(false);
          }}
        />
      ))}
    </Menu>
  );
};

const MetricRow: React.FC<{
  categories: ReviewCategory[];
  index: number;
  items: ReviewItem[];
  metric: CumulativeReportMetric;
  onChange: (metric: CumulativeReportMetric) => void;
  onRemove: () => void;
  questions: MetricQuestion[];
}> = ({ categories, index, items, metric, onChange, onRemove, questions }) => {
  const [kindMenuVisible, setKindMenuVisible] = useState(false);
  const [operatorMenuVisible, setOperatorMenuVisible] = useState(false);
  const scope = metricScope(metric);
  const isScopedQuestionMetric = metric.kind === 'numeric_question_sum' ||
    metric.kind === 'question_match_count' || metric.kind === 'conditional_numeric_sum';
  const scopedQuestions = isScopedQuestionMetric
    ? questionsForMetricScope(metric, questions, categories)
    : [];
  const primaryQuestions = isScopedQuestionMetric
    ? compatiblePrimaryQuestions(metric, scopedQuestions)
    : [];
  const selectedPrimaryQuestion = primaryQuestions.find(question =>
    question.id === primaryQuestionId(metric)
  );
  const conditionQuestions = metric.kind === 'conditional_numeric_sum' && selectedPrimaryQuestion
    ? scopedQuestions.filter(question => question.itemId === selectedPrimaryQuestion.itemId)
    : [];
  const sourceOptions = metric.kind === 'completed_item_count'
    ? items.map(item => ({ id: String(item.id), label: item.name }))
    : metric.kind === 'completed_category_count'
      ? categories.map(category => ({ id: String(category.id), label: category.name }))
      : primaryQuestions.map(question => ({
        id: `${question.itemId}:${question.id}`,
        label: question.label,
      }));

  const selectedSource = (() => {
    if (metric.kind === 'completed_item_count') return String(metric.itemId);
    if (metric.kind === 'completed_category_count') return String(metric.categoryId);
    if (metric.kind === 'question_match_count') {
      return selectedPrimaryQuestion
        ? `${selectedPrimaryQuestion.itemId}:${selectedPrimaryQuestion.id}`
        : '';
    }
    if (metric.kind === 'numeric_question_sum' || metric.kind === 'conditional_numeric_sum') {
      return selectedPrimaryQuestion
        ? `${selectedPrimaryQuestion.itemId}:${selectedPrimaryQuestion.id}`
        : '';
    }
    return '';
  })();

  const condition = metric.kind === 'question_match_count'
    ? metric.match
    : metric.kind === 'conditional_numeric_sum'
      ? metric.when
      : undefined;

  const updateCondition = (next: CumulativeReportMatchCondition) => {
    if (metric.kind === 'question_match_count') onChange({ ...metric, match: next });
    if (metric.kind === 'conditional_numeric_sum') onChange({ ...metric, when: next });
  };

  const selectPrimarySource = (sourceId: string) => {
    if (metric.kind === 'completed_item_count') {
      onChange({ ...metric, itemId: Number(sourceId) });
      return;
    }
    if (metric.kind === 'completed_category_count') {
      onChange({ ...metric, categoryId: Number(sourceId) });
      return;
    }
    const [itemIdText, ...questionParts] = sourceId.split(':');
    const questionId = questionParts.join(':');
    const itemId = Number(itemIdText);
    const scoped = scope === 'item' ? { itemId } : {};
    if (metric.kind === 'numeric_question_sum') {
      onChange({ ...metric, ...scoped, questionId });
    } else if (metric.kind === 'conditional_numeric_sum') {
      const conditionOptions = scopedQuestions.filter(question => question.itemId === itemId);
      const condition = conditionOptions.find(question => question.id === metric.when.questionId) ||
        conditionOptions[0];
      onChange({
        ...metric,
        ...scoped,
        questionId,
        when: { ...metric.when, questionId: condition?.id || '' },
      });
    } else if (metric.kind === 'question_match_count') {
      onChange({ ...metric, ...scoped, match: { ...metric.match, questionId } });
    }
  };

  const switchKind = (kind: MetricKind) => {
    const replacement = createMetric(kind, metric.id, items, categories, questions);
    onChange({ ...replacement, label: metric.label });
    setKindMenuVisible(false);
  };

  return (
    <Card testID={`admin-report-metric-row-${index}`} style={styles.metricCard}>
      <Card.Content>
        <View style={styles.metricHeader}>
          <Text style={styles.metricNumber}>Metric {index + 1}</Text>
          <Button
            testID={`admin-report-metric-${index}-remove`}
            compact
            textColor={colors.error}
            onPress={onRemove}
          >
            Remove
          </Button>
        </View>
        <TextInput
          testID={`admin-report-metric-${index}-label`}
          label="Report label"
          mode="outlined"
          value={metric.label}
          onChangeText={label => onChange({ ...metric, label })}
          style={styles.field}
        />
        <Menu
          visible={kindMenuVisible}
          onDismiss={() => setKindMenuVisible(false)}
          anchor={(
            <Button
              testID={`admin-report-metric-${index}-kind`}
              mode="outlined"
              onPress={() => setKindMenuVisible(true)}
              style={styles.field}
            >
              {metricKindLabel(metric.kind)}
            </Button>
          )}
        >
          {metricKinds.map(option => (
            <Menu.Item
              key={option.kind}
              title={option.label}
              onPress={() => switchKind(option.kind)}
            />
          ))}
        </Menu>

        {isScopedQuestionMetric && (
          <View style={styles.scopeRow}>
            <Text style={styles.controlLabel}>Count within</Text>
            <Button
              testID={`admin-report-metric-${index}-scope-item`}
              compact
              mode={scope === 'item' ? 'contained' : 'outlined'}
              onPress={() => onChange(changeMetricScope(
                metric,
                'item',
                items,
                categories,
                questions
              ))}
            >
              Item
            </Button>
            <Button
              testID={`admin-report-metric-${index}-scope-category`}
              compact
              mode={scope === 'category' ? 'contained' : 'outlined'}
              onPress={() => onChange(changeMetricScope(
                metric,
                'category',
                items,
                categories,
                questions
              ))}
            >
              Category
            </Button>
          </View>
        )}

        {isScopedQuestionMetric && scope === 'category' && (
          <MetricSourceMenu
            testID={`admin-report-metric-${index}-scope-source`}
            selectedId={String('categoryId' in metric ? metric.categoryId : '')}
            options={categories.map(category => ({ id: String(category.id), label: category.name }))}
            onSelect={categoryId => onChange(resetMetricQuestionIds(
              { ...metric, categoryId: Number(categoryId) } as CumulativeReportMetric,
              questions,
              categories,
              false
            ))}
          />
        )}

        <Text style={styles.sourceLabel}>
          {metric.kind === 'completed_item_count' ? 'Item' :
            metric.kind === 'completed_category_count' ? 'Category' : 'Question'}
        </Text>
        <MetricSourceMenu
          testID={`admin-report-metric-${index}-source`}
          selectedId={selectedSource}
          options={sourceOptions}
          onSelect={selectPrimarySource}
        />

        {'categoryId' in metric && metric.categoryId !== undefined && (
          <View style={styles.switchRow}>
            <Text style={styles.controlLabel}>Include subcategories</Text>
            <Switch
              testID={`admin-report-metric-${index}-include-descendants`}
              value={Boolean(metric.includeDescendants)}
              onValueChange={includeDescendants => onChange(resetMetricQuestionIds(
                { ...metric, includeDescendants },
                questions,
                categories,
                true
              ))}
            />
          </View>
        )}

        {metric.kind === 'conditional_numeric_sum' && (
          <>
            <Text style={styles.sourceLabel}>Only when this question matches</Text>
            <MetricSourceMenu
              testID={`admin-report-metric-${index}-condition-source`}
              selectedId={(() => {
                const match = conditionQuestions.find(question =>
                  question.id === metric.when.questionId
                );
                return match ? `${match.itemId}:${match.id}` : '';
              })()}
              options={conditionQuestions.map(question => ({
                id: `${question.itemId}:${question.id}`,
                label: question.label,
              }))}
              onSelect={sourceId => {
                const [, ...questionParts] = sourceId.split(':');
                updateCondition({ ...metric.when, questionId: questionParts.join(':') });
              }}
            />
          </>
        )}

        {condition && (
          <>
            <Menu
              visible={operatorMenuVisible}
              onDismiss={() => setOperatorMenuVisible(false)}
              anchor={(
                <Button
                  testID={`admin-report-metric-${index}-operator`}
                  mode="outlined"
                  onPress={() => setOperatorMenuVisible(true)}
                  style={styles.field}
                >
                  {operatorLabel(condition.operator)}
                </Button>
              )}
            >
              {operators.map(option => (
                <Menu.Item
                  key={option.operator}
                  title={option.label}
                  onPress={() => {
                    const next = { ...condition, operator: option.operator };
                    if (option.operator === 'truthy' || option.operator === 'falsy') delete next.value;
                    else if (option.operator === 'in' && !Array.isArray(next.value)) next.value = [];
                    updateCondition(next);
                    setOperatorMenuVisible(false);
                  }}
                />
              ))}
            </Menu>
            {condition.operator !== 'truthy' && condition.operator !== 'falsy' && (
              <TextInput
                testID={`admin-report-metric-${index}-match-value`}
                label={condition.operator === 'in' ? 'Values (comma separated)' : 'Match value'}
                mode="outlined"
                value={conditionValueText(condition)}
                onChangeText={value => updateCondition(setConditionValue(condition, value))}
                style={styles.field}
              />
            )}
            <View style={styles.switchRow}>
              <Text style={styles.controlLabel}>Ignore capitalization</Text>
              <Switch
                value={Boolean(condition.caseInsensitive)}
                onValueChange={caseInsensitive => updateCondition({ ...condition, caseInsensitive })}
              />
            </View>
          </>
        )}
      </Card.Content>
    </Card>
  );
};

const MetricPreview: React.FC<{ metric: CumulativeReportMetricResult }> = ({ metric }) => (
  <View style={styles.previewMetricRow}>
    <Text style={styles.previewMetricLabel}>{metric.label}</Text>
    <Text testID={`admin-cumulative-preview-metric-${metric.id}`} style={styles.previewMetricValue}>
      {metric.value.toLocaleString()}
    </Text>
  </View>
);

export const AdminCumulativeReportsSection: React.FC<AdminCumulativeReportsSectionProps> = ({
  categories,
  items,
  onTemplateUpdated,
  template,
}) => {
  const revision = useMemo(() => latestRevision(template), [template]);
  const firstRevision = useMemo(() => initialRevision(template), [template]);
  const hasRevisionHistory = Boolean(template.cumulativeReportConfig?.revisions?.length);
  const activeItems = useMemo(
    () => items.filter(item => !item.isDeprecated && !item.deprecatedAt),
    [items]
  );
  const questions = useMemo<MetricQuestion[]>(() => activeItems.flatMap(item =>
    (Array.isArray(item.questionsSchema?.fields) ? item.questionsSchema.fields : [])
      .filter((field: QuestionField) => !field.deprecated)
      .map((field: QuestionField) => ({
        categoryId: item.categoryId,
        id: field.id,
        itemId: item.id,
        label: `${item.name} · ${field.label || field.id}`,
        type: field.type,
      }))
  ), [activeItems]);
  const metricSequence = useRef(0);
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [metricSectionTitle, setMetricSectionTitle] = useState('');
  const [quotedItemsTitle, setQuotedItemsTitle] = useState('');
  const [metrics, setMetrics] = useState<CumulativeReportMetric[]>([]);
  const [month, setMonth] = useState(currentMonth);
  const [effectiveFromDate, setEffectiveFromDate] = useState(() => `${currentMonth()}-01`);
  const [effectiveFromTouched, setEffectiveFromTouched] = useState(false);
  const [preview, setPreview] = useState<CumulativeReportData | null>(null);
  const [busyAction, setBusyAction] = useState<'save' | 'preview' | 'download' | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setEnabled(Boolean(revision?.enabled));
    setTitle(revision?.title || '');
    setMetricSectionTitle(revision?.metricSectionTitle || '');
    setQuotedItemsTitle(revision?.quotedItemsTitle || '');
    setMetrics(cloneMetrics(revision?.metrics));
    setEffectiveFromDate(firstRevision
      ? new Date(firstRevision.effectiveFrom).toISOString().slice(0, 10)
      : `${month}-01`);
    setEffectiveFromTouched(false);
    setPreview(null);
  }, [template.id, revision?.revisionId, firstRevision?.revisionId]);

  useEffect(() => {
    setStatus('');
    setError('');
  }, [template.id]);

  const showError = (caught: unknown, fallback: string) => {
    setError(caught instanceof Error ? caught.message : fallback);
    setStatus('');
  };

  const addMetric = () => {
    metricSequence.current += 1;
    setMetrics(current => [
      ...current,
      createMetric(
        'completed_item_count',
        `metric-${Date.now()}-${metricSequence.current}`,
        activeItems,
        categories,
        questions
      ),
    ]);
  };

  const selectMonth = (nextMonth: string) => {
    setMonth(nextMonth);
    if (hasRevisionHistory || effectiveFromTouched) return;
    try {
      cumulativeReportPeriodForMonth(nextMonth);
      setEffectiveFromDate(`${nextMonth.trim()}-01`);
    } catch {
      // Keep the last valid derived date while the month field is incomplete.
    }
  };

  const save = async () => {
    if (enabled && metrics.length === 0) {
      setError('Add at least one metric before enabling this report.');
      return;
    }
    if (metrics.some(metric => !metric.label.trim())) {
      setError('Every metric needs a report label.');
      return;
    }
    if (metrics.some(metric => !metricHasValidReferences(
      metric,
      activeItems,
      categories,
      questions
    ))) {
      setError('Choose a valid item, category, and question source for every metric.');
      return;
    }

    let effectiveFrom: string | undefined;
    if (!hasRevisionHistory) {
      try {
        effectiveFrom = cumulativeReportEffectiveFromForDate(effectiveFromDate);
      } catch (caught) {
        showError(caught, 'Choose a valid reporting start date.');
        return;
      }
    }

    setBusyAction('save');
    setError('');
    try {
      const cumulativeReportConfig: CumulativeReportConfigRevisionInput = {
        ...(effectiveFrom ? { effectiveFrom } : {}),
        enabled,
        title: title.trim() || undefined,
        metricSectionTitle: metricSectionTitle.trim() || undefined,
        quotedItemsTitle: quotedItemsTitle.trim() || undefined,
        metrics,
      };
      const updated = await adminService.updateTemplate<ReviewTemplate>(template.id, {
        cumulativeReportConfig,
      });
      onTemplateUpdated(updated);
      setStatus('Report configuration saved');
      setPreview(null);
    } catch (caught) {
      showError(caught, 'Failed to save report configuration.');
    } finally {
      setBusyAction(null);
    }
  };

  const loadPreview = async () => {
    setBusyAction('preview');
    setError('');
    try {
      const data = await adminCumulativeReportService.preview(
        template.id,
        cumulativeReportPeriodForMonth(month)
      );
      setPreview(data);
      setStatus('Preview updated');
    } catch (caught) {
      showError(caught, 'Failed to preview cumulative report.');
    } finally {
      setBusyAction(null);
    }
  };

  const download = async () => {
    setBusyAction('download');
    setError('');
    try {
      const result = await adminCumulativeReportService.downloadAndShare(
        template.id,
        template.name,
        month,
        cumulativeReportPeriodForMonth(month)
      );
      setStatus(`PDF ready: ${result.fileName}`);
    } catch (caught) {
      showError(caught, 'Failed to download cumulative report.');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <View testID="admin-cumulative-reports-section">
      <View style={styles.reportIntro}>
        <Text style={styles.eyebrow}>MONTHLY LOSS PREVENTION LEDGER</Text>
        <Text style={styles.heading}>Cumulative reports</Text>
        <Text style={styles.introCopy}>
          Choose what this template counts, then preview or share one calendar month at a time.
        </Text>
      </View>

      <Card style={styles.sectionCard}>
        <Card.Content>
          <View style={styles.switchRow}>
            <View style={styles.switchCopy}>
              <Text style={styles.sectionTitle}>Report configuration</Text>
              <Text style={styles.hint}>Changes take effect as a new dated revision.</Text>
            </View>
            <Switch
              testID="admin-cumulative-report-enabled"
              value={enabled}
              onValueChange={setEnabled}
            />
          </View>
          <TextInput
            testID="admin-cumulative-report-effective-from"
            label={hasRevisionHistory ? 'Reporting began (UTC)' : 'Reporting begins (UTC)'}
            placeholder="YYYY-MM-DD"
            mode="outlined"
            value={effectiveFromDate}
            onChangeText={value => {
              setEffectiveFromDate(value);
              setEffectiveFromTouched(true);
            }}
            disabled={hasRevisionHistory}
            autoCapitalize="none"
            style={styles.field}
          />
          <Text style={styles.effectiveDateHint}>
            {hasRevisionHistory
              ? 'The initial date is locked. New saves append a later configuration revision.'
              : 'The first revision defaults to the selected preview month so prior months can be backfilled.'}
          </Text>
          <TextInput
            testID="admin-cumulative-report-title"
            label="Report title"
            placeholder="Exhale – By the Numbers"
            mode="outlined"
            value={title}
            onChangeText={setTitle}
            style={styles.field}
          />
          <TextInput
            testID="admin-cumulative-report-metric-section-title"
            label="Metrics section title"
            placeholder={`${template.name} visit results`}
            mode="outlined"
            value={metricSectionTitle}
            onChangeText={setMetricSectionTitle}
            style={styles.field}
          />
          <TextInput
            testID="admin-cumulative-report-quoted-items-title"
            label="Quoted items section title"
            placeholder="Quoted items and on-demand projects"
            mode="outlined"
            value={quotedItemsTitle}
            onChangeText={setQuotedItemsTitle}
            style={styles.field}
          />
        </Card.Content>
      </Card>

      <View style={styles.metricsHeadingRow}>
        <View style={styles.switchCopy}>
          <Text style={styles.sectionTitle}>Metric rows</Text>
          <Text style={styles.hint}>{metrics.length} configured</Text>
        </View>
        <Button
          testID="admin-cumulative-report-add-metric"
          mode="contained-tonal"
          icon="plus"
          disabled={activeItems.length === 0}
          onPress={addMetric}
        >
          Add metric
        </Button>
      </View>

      {metrics.length === 0 ? (
        <Card style={styles.emptyCard}>
          <Card.Content>
            <Text style={styles.emptyTitle}>No metric rows yet</Text>
            <Text style={styles.hint}>Add a row to count completed work or aggregate question answers.</Text>
          </Card.Content>
        </Card>
      ) : metrics.map((metric, index) => (
        <MetricRow
          key={metric.id}
          categories={categories}
          index={index}
          items={activeItems}
          metric={metric}
          questions={questions}
          onChange={next => setMetrics(current => current.map((entry, entryIndex) =>
            entryIndex === index ? next : entry
          ))}
          onRemove={() => setMetrics(current => current.filter((_, entryIndex) => entryIndex !== index))}
        />
      ))}

      <Button
        testID="admin-cumulative-report-save"
        mode="contained"
        icon="content-save-outline"
        loading={busyAction === 'save'}
        disabled={busyAction !== null}
        onPress={save}
        style={styles.primaryAction}
      >
        Save report configuration
      </Button>

      <Divider style={styles.divider} />

      <Card style={styles.sectionCard}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Preview a calendar month</Text>
          <View style={styles.monthRow}>
            <Button
              testID="admin-cumulative-report-previous-month"
              compact
              icon="chevron-left"
              onPress={() => selectMonth(shiftCumulativeReportMonth(month, -1))}
            >
              Previous
            </Button>
            <TextInput
              testID="admin-cumulative-report-month"
              label="Month"
              mode="outlined"
              value={month}
              onChangeText={selectMonth}
              style={styles.monthInput}
            />
            <Button
              testID="admin-cumulative-report-next-month"
              compact
              contentStyle={styles.nextMonthContent}
              icon="chevron-right"
              onPress={() => selectMonth(shiftCumulativeReportMonth(month, 1))}
            >
              Next
            </Button>
          </View>
          <View style={styles.actionRow}>
            <Button
              testID="admin-cumulative-report-preview-button"
              mode="outlined"
              icon="eye-outline"
              loading={busyAction === 'preview'}
              disabled={busyAction !== null}
              onPress={loadPreview}
              style={styles.actionButton}
            >
              Preview totals
            </Button>
            <Button
              testID="admin-cumulative-report-download-button"
              mode="contained"
              icon="file-pdf-box"
              loading={busyAction === 'download'}
              disabled={busyAction !== null}
              onPress={download}
              style={styles.actionButton}
            >
              Download / share PDF
            </Button>
          </View>
        </Card.Content>
      </Card>

      {error ? (
        <Text testID="admin-cumulative-report-error" style={styles.error}>{error}</Text>
      ) : null}
      {status ? (
        <Text testID="admin-cumulative-report-status" style={styles.status}>{status}</Text>
      ) : null}

      {preview && (
        <Card testID="admin-cumulative-report-preview" style={styles.previewCard}>
          <Card.Content>
            <Text style={styles.previewTitle}>{preview.title}</Text>
            <Text style={styles.previewPeriod}>{month} · {preview.templateName}</Text>
            <View style={styles.ledgerRow}>
              <View style={styles.ledgerCell}>
                <Text testID="admin-cumulative-preview-reviews" style={styles.ledgerValue}>
                  {preview.engagement.reviews}
                </Text>
                <Text style={styles.ledgerLabel}>Reviews</Text>
              </View>
              <View style={styles.ledgerCell}>
                <Text testID="admin-cumulative-preview-members" style={styles.ledgerValue}>
                  {preview.engagement.distinctMembers}
                </Text>
                <Text style={styles.ledgerLabel}>Members</Text>
              </View>
              <View style={styles.ledgerCell}>
                <Text testID="admin-cumulative-preview-properties" style={styles.ledgerValue}>
                  {preview.engagement.distinctProperties}
                </Text>
                <Text style={styles.ledgerLabel}>Properties</Text>
              </View>
            </View>
            <Text style={styles.previewSectionTitle}>{preview.metricSectionTitle}</Text>
            {preview.metrics.map(metric => <MetricPreview key={metric.id} metric={metric} />)}
            <Text style={styles.quotedSummary}>
              {preview.quotedItems.length} quoted item{preview.quotedItems.length === 1 ? '' : 's'}
            </Text>
          </Card.Content>
        </Card>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  reportIntro: {
    borderLeftColor: colors.primary,
    borderLeftWidth: 5,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.lg,
  },
  eyebrow: {
    ...typography.caption,
    color: colors.primaryDark,
    fontWeight: '700',
    letterSpacing: 1.2,
  },
  heading: {
    ...typography.h2,
    color: colors.text,
    marginTop: spacing.xs,
  },
  introCopy: {
    ...typography.body2,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  sectionCard: {
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.text,
  },
  hint: {
    ...typography.body2,
    color: colors.textSecondary,
  },
  field: {
    marginTop: spacing.sm,
  },
  effectiveDateHint: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  switchRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginVertical: spacing.sm,
  },
  switchCopy: {
    flex: 1,
  },
  metricsHeadingRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  metricCard: {
    backgroundColor: colors.gray50,
    borderColor: colors.border,
    borderWidth: 1,
    marginBottom: spacing.md,
  },
  metricHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metricNumber: {
    ...typography.bodyBold,
    color: colors.gray700,
  },
  scopeRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginVertical: spacing.sm,
  },
  controlLabel: {
    ...typography.body2,
    color: colors.text,
  },
  sourceLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '600',
    marginBottom: spacing.xs,
    marginTop: spacing.sm,
  },
  emptyCard: {
    backgroundColor: colors.primaryLight,
    marginBottom: spacing.md,
  },
  emptyTitle: {
    ...typography.bodyBold,
    color: colors.text,
  },
  primaryAction: {
    marginTop: spacing.sm,
  },
  divider: {
    marginVertical: spacing.xl,
  },
  monthRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  monthInput: {
    flex: 1,
    minWidth: 130,
  },
  nextMonthContent: {
    flexDirection: 'row-reverse',
  },
  actionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  actionButton: {
    flexGrow: 1,
  },
  error: {
    ...typography.body2,
    color: colors.error,
    marginBottom: spacing.md,
  },
  status: {
    ...typography.body2,
    color: colors.success,
    fontWeight: '600',
    marginBottom: spacing.md,
  },
  previewCard: {
    backgroundColor: colors.secondary,
    marginBottom: spacing.xl,
  },
  previewTitle: {
    ...typography.h3,
    color: '#ffffff',
  },
  previewPeriod: {
    ...typography.caption,
    color: colors.gray300,
    marginBottom: spacing.md,
    marginTop: spacing.xs,
  },
  ledgerRow: {
    borderColor: colors.gray600,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    flexDirection: 'row',
    marginBottom: spacing.md,
  },
  ledgerCell: {
    alignItems: 'center',
    flex: 1,
    paddingVertical: spacing.md,
  },
  ledgerValue: {
    color: '#ffffff',
    fontSize: 26,
    fontWeight: '700',
  },
  ledgerLabel: {
    ...typography.caption,
    color: colors.gray300,
    textTransform: 'uppercase',
  },
  previewSectionTitle: {
    ...typography.bodyBold,
    color: '#ffffff',
    marginBottom: spacing.xs,
  },
  previewMetricRow: {
    alignItems: 'center',
    borderBottomColor: colors.gray700,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
  },
  previewMetricLabel: {
    ...typography.body2,
    color: colors.gray200,
    flex: 1,
  },
  previewMetricValue: {
    ...typography.h4,
    color: colors.primaryLight,
  },
  quotedSummary: {
    ...typography.caption,
    color: colors.gray300,
    marginTop: spacing.md,
  },
});

export default AdminCumulativeReportsSection;
